/**
 * The personal layout of the signed-in Home: which sections show and in what order. Stored on the
 * account (`users.home_layout`), so it follows the user across browsers. The stored form may be
 * stale (a retired section id) or incomplete (a section added later): `normalizeHomeLayout` always
 * yields every CURRENT section exactly once, so Home never loses a section by accident.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api';
import { dropMove } from '@/shared/lib/reorder';

import { updateViewerUser, useViewer } from './model';
import type { User } from './types';

export const HOME_WIDGETS = [
  'my_work',
  'calendar',
  'workspaces',
  'groups',
  'boards',
  'databases',
  'documents',
] as const;
export type HomeWidget = (typeof HOME_WIDGETS)[number];

export interface HomeLayout {
  order: HomeWidget[];
  hidden: HomeWidget[];
}

export const DEFAULT_HOME_LAYOUT: HomeLayout = {
  // Phone order: My work, then the catalog sections, then the calendar - as before.
  order: ['my_work', 'workspaces', 'groups', 'boards', 'databases', 'documents', 'calendar'],
  hidden: [],
};

const isWidget = (value: string): value is HomeWidget =>
  (HOME_WIDGETS as readonly string[]).includes(value);

export function normalizeHomeLayout(
  stored: { order: string[]; hidden: string[] } | null | undefined,
): HomeLayout {
  if (!stored) return { order: [...DEFAULT_HOME_LAYOUT.order], hidden: [] };
  const order: HomeWidget[] = [];
  for (const id of stored.order) if (isWidget(id) && !order.includes(id)) order.push(id);
  // A section the stored layout does not mention (added later) takes its default place.
  for (const id of DEFAULT_HOME_LAYOUT.order) if (!order.includes(id)) order.push(id);
  const hidden = stored.hidden.filter(
    (id, index, all): id is HomeWidget => isWidget(id) && all.indexOf(id) === index,
  );
  return { order, hidden };
}

export const isDefaultHomeLayout = (layout: HomeLayout): boolean =>
  layout.hidden.length === 0 &&
  layout.order.every((id, index) => id === DEFAULT_HOME_LAYOUT.order[index]);

/** Move one section a step up (`-1`) or down (`1`); at an end nothing changes. */
export function moveWidget(layout: HomeLayout, id: HomeWidget, step: -1 | 1): HomeLayout {
  const from = layout.order.indexOf(id);
  const to = from + step;
  if (from < 0 || to < 0 || to >= layout.order.length) return layout;
  const order = [...layout.order];
  [order[from], order[to]] = [order[to] as HomeWidget, order[from] as HomeWidget];
  return { ...layout, order };
}

/** Put one section before/after another (a drop); the same layout back when nothing would change. */
export function placeWidget(
  layout: HomeLayout,
  id: HomeWidget,
  targetId: HomeWidget,
  position: 'before' | 'after',
): HomeLayout {
  const moved = dropMove(layout.order, id, targetId, position, 'widget');
  return moved ? { ...layout, order: moved.order as HomeWidget[] } : layout;
}

export function toggleWidget(layout: HomeLayout, id: HomeWidget): HomeLayout {
  const hidden = layout.hidden.includes(id)
    ? layout.hidden.filter((item) => item !== id)
    : [...layout.hidden, id];
  return { ...layout, hidden };
}

/** The viewer's layout and the ways to change it. Changes show at once and are saved to the
 * account; a failed save puts the stored layout back. */
export function useHomeLayout() {
  const viewer = useViewer();
  const client = useQueryClient();
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  const layout = normalizeHomeLayout(user?.home_layout);
  const save = useMutation({
    mutationFn: (next: HomeLayout | null) => api.patch<User>('/v1/account', { home_layout: next }),
    onSuccess: (saved) => {
      updateViewerUser(client, saved);
    },
  });
  // The change shows at once (synchronously, in the cache) and is then saved; a failed save puts
  // the previous layout back.
  const apply = (next: HomeLayout | null) => {
    if (!user) return;
    updateViewerUser(client, { ...user, home_layout: next });
    save.mutate(next, {
      onError: () => {
        updateViewerUser(client, user);
      },
    });
  };
  return {
    layout,
    isDefault: isDefaultHomeLayout(layout),
    saving: save.isPending,
    error: save.error,
    change: (next: HomeLayout) => {
      apply(next);
    },
    reset: () => {
      apply(null);
    },
  };
}
